import * as React from 'react'
import * as mapboxgl from 'mapbox-gl'

import { MapContextProvider, Registerable, Setup } from './context'
import { getViewport, Viewport } from './utils'

export type MapboxMapProps = {
  options: Omit<mapboxgl.MapboxOptions, 'container'> & {
    performanceMetricsCollection?: boolean
    viewport?: Viewport
  }
  hidePOI?: boolean
  onError?: (error: any) => void
  scaleControl?:
    | boolean
    | {
        maxWidth?: number | undefined
        unit?: string | undefined
      }
  navigationControl?:
    | boolean
    | {
        showCompass?: boolean | undefined
        showZoom?: boolean | undefined
        visualizePitch?: boolean | undefined
      }
} & React.ComponentProps<'div'>

export function MapboxMap({
  options,
  children,
  onError,
  navigationControl = true,
  hidePOI = false,
  scaleControl = true,
  ...divProps
}: MapboxMapProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const mapRef = React.useRef<{ loaded: Promise<mapboxgl.Map>; map: mapboxgl.Map }>()
  const scaleControlRef = React.useRef<mapboxgl.ScaleControl>()
  const navigationControlRef = React.useRef<mapboxgl.NavigationControl>()
  const [version, setVersion] = React.useState(0)

  const registeredRef = React.useRef<Map<string, Registerable>>(new Map())
  const stackRef = React.useRef<Setup[]>([])

  React.useEffect(() => {
    if (containerRef.current === null) {
      return
    }

    if (mapRef.current === undefined) {
      try {
        mapRef.current = create(containerRef.current)
      } catch (error) {
        onError?.(error)
      }
    }
  }, [])

  React.useEffect(() => {
    if (mapRef.current === undefined) {
      return
    }

    const map = mapRef.current.map

    let currentSetup
    while ((currentSetup = stackRef.current.pop()) !== undefined) {
      const { id, value } = currentSetup({ map })
      registeredRef.current.set(id, value)
    }

    const coordinates: mapboxgl.LngLat[] = []
    for (const entry of registeredRef.current.values()) {
      coordinates.push(entry.getLngLat())
    }

    const viewport = getViewport(coordinates)

    if (viewport?.type === 'bounds') {
      map.fitBounds(viewport.bounds, viewport.options)
    } else if (viewport?.type === 'center') {
      map.flyTo(viewport.options)
    }
  }, [version])

  React.useEffect(() => {
    if (mapRef.current === undefined) {
      return
    }

    mapRef.current.loaded.then((map) => {
      map.setLayoutProperty('poi-label', 'visibility', hidePOI ? 'none' : 'visible')
    })
  }, [hidePOI])

  React.useEffect(() => {
    if (mapRef.current === undefined) {
      return
    }

    const map = mapRef.current.map
    if (scaleControlRef.current !== undefined) {
      map.removeControl(scaleControlRef.current)
    }

    if (scaleControl) {
      scaleControlRef.current = new mapboxgl.ScaleControl(scaleControl !== true ? scaleControl : undefined)
      map.addControl(scaleControlRef.current)
    }
  }, [scaleControl])

  React.useEffect(() => {
    if (mapRef.current === undefined) {
      return
    }

    const map = mapRef.current.map
    if (navigationControlRef.current !== undefined) {
      map.removeControl(navigationControlRef.current)
    }

    if (navigationControl) {
      navigationControlRef.current = new mapboxgl.NavigationControl(
        navigationControl !== true ? navigationControl : undefined,
      )
      map.addControl(navigationControlRef.current)
    }
  }, [navigationControl])

  return (
    <div ref={containerRef} {...divProps}>
      <MapContextProvider
        value={{
          push: (setup) => {
            setVersion((version) => version + 1)
            stackRef.current.push(setup)
          },
          pop: (id) => {
            setVersion((version) => version + 1)
            registeredRef.current.delete(id)
          },
          registered: registeredRef.current,
        }}
      >
        {children}
      </MapContextProvider>
    </div>
  )

  function create(container: HTMLElement) {
    const map = new mapboxgl.Map({
      performanceMetricsCollection: false,
      projection: { name: 'mercator' },
      cooperativeGestures: true,
      pitchWithRotate: false,
      ...options,
      bounds: options.viewport?.type === 'bounds' ? options.viewport.bounds : undefined,
      fitBoundsOptions: options.viewport?.type === 'bounds' ? options.viewport.options : undefined,
      center: options.viewport?.type === 'center' ? options.viewport.options.center : options.center ?? [0, 0],
      zoom: options.viewport?.type === 'center' ? options.viewport.options.zoom : 15,
      container,
    })

    return {
      loaded: new Promise<mapboxgl.Map>((res) => {
        map.on('load', () => res(map))
      }),
      map,
    }
  }
}
